import { useRef, useState, useCallback } from 'react';

interface UseSwipeResult {
  swipeRef: React.RefObject<HTMLDivElement | null>;
  swipeOffset: number;
  isRevealed: boolean;
  resetSwipe: () => void;
  swipeHandlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
}

export function useSwipe(onSwipeLeft?: () => void, threshold = 80): UseSwipeResult {
  const swipeRef = useRef<HTMLDivElement | null>(null);
  const startXRef = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);

  const resetSwipe = useCallback(() => {
    setSwipeOffset(0);
    setIsRevealed(false);
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
  }, []);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const deltaX = e.touches[0].clientX - startXRef.current;
      if (deltaX < 0) {
        // Swiping left — clamp to threshold
        const offset = Math.max(deltaX, -threshold);
        setSwipeOffset(offset);
      }
    },
    [threshold]
  );

  const onTouchEnd = useCallback(() => {
    if (swipeOffset <= -threshold) {
      setIsRevealed(true);
      setSwipeOffset(-threshold);
      onSwipeLeft?.();
    } else {
      // Snap back
      setSwipeOffset(0);
      setIsRevealed(false);
    }
  }, [swipeOffset, threshold, onSwipeLeft]);

  return {
    swipeRef,
    swipeOffset,
    isRevealed,
    resetSwipe,
    swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
